import type { SportTreeItem } from '@bz/request/src/apis';

import type { SportType } from '~/constants';
import { EventStatus, EventStatusName, SportIdMap, SportTypeMap, SPORTS_RADAR_LANGS } from '~/constants';

/**
 * Format number to string with specified digits, padding with 0 if needed
 * @param num The number to format
 * @returns Formatted string with specified digits
 * @example
 * formatTwoDigit(1) // "01"
 * formatTwoDigit(12) // "12"
 * formatTwoDigit(123) // "123"
 */
export function formatTwoDigit(num: number, digits = 2): string {
  return num.toString().padStart(digits, '0');
}

/**
 * Format number with specified decimal places
 * @param num
 * @param digits
 * @returns
 */
export function sliceNumber(num: number, digits = 2): number {
  return Number(
    String(num)
      .split('.')
      .map((it, index) => (index === 0 ? it : it.slice(0, digits)))
      .join('.')
  );
}

/**
 * Simple try-catch wrapper
 * @param fn Function to execute
 * @param fallback Error handler function
 * @returns Result of fn or fallback
 */
export function tryCatch<T>(fn: () => T, fallback?: (err: Error) => T) {
  try {
    return fn();
  } catch (error) {
    return fallback?.(error as Error);
  }
}

/**
 * Extract id from urn
 * @param urn
 * @example
 * getIdFromUrn('sr:sport:1') // sr:sport:1 -> '1'
 * getIdFromUrn('sr:category:43') // sr:category:43 -> '43'
 * getIdFromUrn('sr:tournament:31') // sr:tournament:31 -> '31'
 * @returns
 */
export function getIdFromUrn(urn: string) {
  const arr = urn.split(':');
  return arr[arr.length - 1] || urn;
}

/**
 * Check event status type
 */
export function checkEventStatus(status: EventStatus) {
  return {
    isClosed: status === EventStatus.CLOSED,
    isEnded: status === EventStatus.ENDED,
    isLive: status === EventStatus.LIVE_EVENTS,
    isUpcoming: status === EventStatus.UPCOMING,
  };
}

/**
 * Convert number to ordinal string (simplified version)
 * 1 -> 1st, 2 -> 2nd, 3 -> 3rd, 4+ -> 4th, 5th, etc.
 */
const getOrdinal = (n: number) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return n + 'th';
};

export const getDynamicPeriodName = (period: any, index?: number) => {
  const rawName = period.matchStatusName || '';
  const fallback = period.matchStatusShortName || (typeof index === 'number' ? getOrdinal(index + 1) : '');

  if (!rawName) return fallback;

  // Handle extra exact match
  const lowerName = rawName.toLowerCase();
  if (lowerName === '1st extra' || lowerName === '2nd extra') {
    return fallback;
  }

  // Handle standard numbered sequences: 1st half, 2nd quarter etc -> 1st, 2nd
  const numberedPrefixMatch = rawName.match(/^(\d+(?:st|nd|rd|th))\b/i);
  if (numberedPrefixMatch) {
    return numberedPrefixMatch[1];
  }

  // Fallback to shortName for any other cases
  return fallback;
};

/**
 * Get sport type by sport ID
 * @param id Sport ID, e.g. 'sr:sport:1'
 * @returns Sport type, e.g. 'SOCCER', returns undefined if not found
 * @example
 * getSportTypeById('sr:sport:1') // sr:sport:1 -> 'SOCCER'
 * getSportTypeById('sr:sport:2') // sr:sport:2 -> 'BASKETBALL'
 * getSportTypeById('sr:sport:16') // sr:sport:16 -> 'AMERICAN_FOOTBALL'
 */
export function getSportTypeById(id: string): SportType | undefined {
  // Iterate through SportIdMap to find the key with matching value
  for (const [key, value] of Object.entries(SportIdMap)) {
    if (value === id) {
      // Return the corresponding SportType value
      return SportTypeMap[key as keyof typeof SportTypeMap] as SportType;
    }
  }
  return undefined;
}

/**
 * Get I18N sport name by sport ID from backend data
 * @param id
 * @param sports Sport list
 * @example
 * getSportI18nNameById('sr:sport:1', globalSports.sports) // 'Soccer'
 * getSportI18nNameById('sr:sport:2', globalSports.sports) // 'Basketball'
 * getSportI18nNameById('sr:sport:16', globalSports.sports) // 'American Football'
 * @returns
 */
export function getSportI18nNameById(id: string, sports: SportTreeItem[]): string | undefined {
  return sports.find((sport) => sport.id === id)?.name;
}

/**
 * Convert name to slug (URL-friendly name)
 * @param name Name
 * @returns
 * @example
 * toSlug('Hello World') => 'hello-world'
 * toSlug('Hello_World') => 'hello-world'
 * toSlug('Hello.World!') => 'hello-world'
 * toSlug('Hello World & More') => 'hello-world-more'
 */
export function toSlug(name: string) {
  return (
    name
      .toLowerCase()
      // Replace special characters with hyphens
      .replace(/[_\.!@#$%^&*()+=\[\]{}|\\:";'<>?,.\/]/g, '-')
      // Replace spaces with hyphens
      .replace(/\s+/g, '-')
      // Remove consecutive hyphens
      .replace(/-+/g, '-')
      // Remove leading and trailing hyphens
      .replace(/^-+|-+$/g, '')
  );
}

/**
 * Get sport route name by sport ID
 * @param id Sport ID, e.g. 'sr:sport:1'
 * @returns Sport route name, e.g. 'soccer', returns undefined if not found
 * @example
 * getSportRouteNameById('sr:sport:1') // SOCCER -> 'soccer'
 * getSportRouteNameById('sr:sport:2') // BASKETBALL -> 'basketball'
 * getSportRouteNameById('sr:sport:16') // AMERICAN_FOOTBALL -> 'american-football'
 */
export function getSportRouteNameById(id: string) {
  return toSlug(getSportTypeById(id) || '');
}

/**
 * Reverse operation of getSportRouteNameById, get sport ID by route name
 * @param name
 * @param sports
 * @returns Sport ID, e.g. 'sr:sport:1', returns undefined if not found
 * @example
 * getSportIdByRouteName('soccer', globalSports.sports) // 'sr:sport:1'
 * getSportIdByRouteName('basketball', globalSports.sports) // 'sr:sport:2'
 * getSportIdByRouteName('american-football', globalSports.sports) // 'sr:sport:16'
 */
export function getSportIdByRouteName(name: string, sports: SportTreeItem[]): string | undefined {
  const find = sports.find((sport) => {
    const routeName = getSportRouteNameById(sport.id);
    return routeName === name;
  });
  return find?.id;
}

/**
 * Get event status by event name
 * @param name Event name
 * @returns Event status
 */
export function getEventStatusByEventName(name: EventStatusName): EventStatus {
  switch (name) {
    case EventStatusName.CLOSED:
      return EventStatus.CLOSED;
    case EventStatusName.ENDED:
      return EventStatus.ENDED;
    case EventStatusName.LIVE_EVENTS:
      return EventStatus.LIVE_EVENTS;
    case EventStatusName.UPCOMING:
      return EventStatus.UPCOMING;
  }
}

/**
 * Get currency icon by currency name
 * @param name
 * @param isIconName
 * @returns
 */
export function getCurrencyIcon(name: string, isIconName = false) {
  const beforeUrl = import.meta.env.VITE_CURRENCY_ICON_URL;
  return isIconName
    ? `${beforeUrl}${name}.white.png`
    : `${beforeUrl}${currencyConfig[name]?.aliasCurrencyName || name}.png`;
}

/**
 * 获取 Sportradar 支持的语言码
 * - zh-TW => zht
 * - xx-YY => xx
 * - 不支持则回退 en
 */
export function getSportsRadarLang(lang: string): (typeof SPORTS_RADAR_LANGS)[number] {
  const normalized = lang === 'zh-TW' ? 'zht' : lang.split('-')[0] || 'en';
  return (SPORTS_RADAR_LANGS.includes(normalized) ? normalized : 'en') as (typeof SPORTS_RADAR_LANGS)[number];
}

export const toTrailingDigits = (v: unknown) => {
  if (v == null) return '';
  if (typeof v === 'number') return String(v);
  const s = String(v);
  return s.match(/(\d+)\s*$/)?.[1] ?? s;
};
/**
 * Get match time display format for hot matches
 * @param time Match scheduled start time (timestamp in milliseconds)
 * @returns Formatted time object { date: string, time: string }
 */
export const getMatchUpcomingTimeFormat = (time?: number) => {
  if (!time) return { date: '', time: '' };

  const now = Date.now();
  const diff = time - now;
  const diffInSeconds = Math.floor(diff / 1000);

  // More than 24 hours before start: show specific date and time
  if (diffInSeconds > 24 * 60 * 60) {
    const date = new Date(time);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return { date: `${month} ${day}`, time: `${hours}:${minutes}`, type: 1 };
  }

  // Last 60 minutes: show countdown to seconds
  if (diffInSeconds <= 60 * 60) {
    return { date: t('Just Started'), time: '', type: 3 };
  }

  // 1-24 hours before start: show countdown to minutes
  const hours = Math.floor(diffInSeconds / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  return { date: `${hours}h ${minutes}m`, time: '', type: 2 };
};

/**
 * Calculate number of fire icons to display based on popularity percentage
 * @param fire Percentage
 */
export const getFireIconCount = (fire?: number | null) => {
  if (!fire) return 0;
  if (fire > 70) return 3;
  if (fire >= 50) return 2;
  if (fire >= 0) return 1;
  return 0;
};

/**
 * @param markets Market list, see MatchMarketResponse.markets in packages/request/src/odds/api.ts
 * @param homeTeamName Home team name, defaults to 'home' if empty
 * @param awayTeamName Away team name, defaults to 'away' if empty
 * @description Modifies the original array directly, does not return a new value
 */
export function replaceMarketPlaceholders(
  markets: any[] | undefined,
  homeTeamName: string | undefined,
  awayTeamName: string | undefined
): void {
  if (!Array.isArray(markets)) return;

  const home = homeTeamName || 'home';
  const away = awayTeamName || 'away';

  for (const market of markets) {
    if (!market) continue;

    // 1. Replace in market.marketName
    if (market.marketName && typeof market.marketName === 'string') {
      market.marketName = market.marketName.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
    }

    // 1.5 Replace in market.popularBetName
    if (market.popularBetName && typeof market.popularBetName === 'string') {
      market.popularBetName = market.popularBetName
        .replace(/\{#competitor1\}/g, home)
        .replace(/\{#competitor2\}/g, away);
    }

    // 2. Replace in market.colNameList
    if (Array.isArray(market.colNameList)) {
      market.colNameList = market.colNameList.map((col: any) => {
        if (typeof col === 'string') {
          return col.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
        }
        return col;
      });
    }

    // 3. Replace in market.marketSpecifierList -> specifier.outcomes
    if (Array.isArray(market.marketSpecifierList)) {
      for (const specifier of market.marketSpecifierList) {
        if (!specifier || !Array.isArray(specifier.outcomes)) continue;

        for (const outcome of specifier.outcomes) {
          if (!outcome) continue;

          // Replace in outcome.outcomeName
          if (outcome.outcomeName && typeof outcome.outcomeName === 'string') {
            outcome.outcomeName = outcome.outcomeName
              .replace(/\{#competitor1\}/g, home)
              .replace(/\{#competitor2\}/g, away);
          }

          // Replace in outcome.displayName
          if (outcome.displayName && typeof outcome.displayName === 'string') {
            outcome.displayName = outcome.displayName
              .replace(/\{#competitor1\}/g, home)
              .replace(/\{#competitor2\}/g, away);
          }
        }
      }
    }
  }
}

export function replaceOutcomesPlaceholders(
  outcomes: any[] | undefined,
  homeTeamName?: string | undefined,
  awayTeamName?: string | undefined
): void {
  if (!Array.isArray(outcomes)) return;

  for (const outcome of outcomes) {
    if (!outcome) continue;

    // Determine home/away team names, prioritize passed parameters, then extract from outcome, finally use defaults
    const home =
      homeTeamName ||
      outcome.homeShortName ||
      outcome.homeName ||
      outcome.matchInfo?.homeShortName ||
      outcome.matchInfo?.homeName ||
      'Home';
    const away =
      awayTeamName ||
      outcome.awayShortName ||
      outcome.awayName ||
      outcome.matchInfo?.awayShortName ||
      outcome.matchInfo?.awayName ||
      'Away';

    // Replace in outcome.outcomeName
    if (outcome.outcomeName && typeof outcome.outcomeName === 'string') {
      outcome.outcomeName = outcome.outcomeName.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
    }

    // Replace in outcome.colName
    if (outcome.colName && typeof outcome.colName === 'string') {
      outcome.colName = outcome.colName.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
    }

    // Replace in outcome.marketName
    if (outcome.marketName && typeof outcome.marketName === 'string') {
      outcome.marketName = outcome.marketName.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
    }
  }
}
